import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ButtonBase, Chip, Typography, useMediaQuery} from '@material-ui/core';
import {makeStyles, useTheme} from '@material-ui/core/styles';

export const createSettingsScaffoldStyles = (theme) => ({
    root: {
        display: 'grid',
        gridTemplateColumns: '248px minmax(0, 1fr)',
        minHeight: 'calc(100vh - 160px)',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
        background: theme.palette.background.paper,
        boxShadow: theme.palette.type === 'dark'
            ? '0 18px 50px rgba(0, 0, 0, 0.22)'
            : '0 18px 50px rgba(38, 42, 51, 0.08)',
        '@media (prefers-reduced-motion: reduce)': {
            '& *, & *::before, & *::after': {
                animationDuration: '0.001ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.001ms !important',
                scrollBehavior: 'auto !important'
            }
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            minHeight: 'auto'
        }
    },
    nav: {
        minWidth: 0,
        padding: theme.spacing(3, 2),
        borderRight: `1px solid ${theme.palette.divider}`,
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0))'
            : 'linear-gradient(180deg, rgba(254,242,0,0.06), rgba(255,255,255,0))',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2, 2, 1.5),
            borderRight: 0,
            borderBottom: `1px solid ${theme.palette.divider}`
        }
    },
    navTitle: {
        marginBottom: theme.spacing(3),
        fontWeight: 800,
        letterSpacing: '-0.025em',
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(1.5)
        }
    },
    tabList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'row',
            overflowX: 'auto',
            paddingBottom: theme.spacing(0.5),
            scrollbarWidth: 'thin'
        }
    },
    navItem: {
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '40px minmax(0, 1fr) auto',
        width: '100%',
        minHeight: 48,
        padding: theme.spacing(0.75, 1.25),
        overflow: 'hidden',
        borderRadius: 11,
        color: theme.palette.text.secondary,
        textAlign: 'left',
        transition: 'transform 120ms cubic-bezier(0.2,0,0,1), background-color 120ms cubic-bezier(0.2,0,0,1), color 120ms cubic-bezier(0.2,0,0,1)',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: 3,
            background: theme.palette.primary.main,
            transform: 'scaleY(0)',
            transition: 'transform 220ms cubic-bezier(0.34,1.35,0.64,1)'
        },
        '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
            transform: 'translateX(2px)',
            '& $navIcon': {
                color: theme.palette.text.primary,
                transform: 'scale(1.03)'
            }
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2
        },
        [theme.breakpoints.down('sm')]: {
            flex: '0 0 auto',
            width: 'auto',
            minWidth: 132,
            gridTemplateColumns: '32px auto',
            '&:hover': {transform: 'translateY(-1px)'}
        }
    },
    navItemActive: {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,0.1)' : 'rgba(254,242,0,0.16)',
        fontWeight: 700,
        '&::before': {transform: 'scaleY(1)'},
        '& $navIcon': {
            color: theme.palette.type === 'dark' ? theme.palette.primary.main : '#A38F00',
            opacity: 1,
            transform: 'scale(1.06)',
        }
    },
    navIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        alignSelf: 'center',
        flexShrink: 0,
        borderRadius: 9,
        color: theme.palette.text.secondary,
        opacity: 0.78,
        transition: 'transform 180ms cubic-bezier(0.2,0,0,1), color 140ms cubic-bezier(0.2,0,0,1), opacity 140ms cubic-bezier(0.2,0,0,1)',
        '& svg': {fontSize: 19}
    },
    navLabel: {
        alignSelf: 'center',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 13
    },
    badge: {
        alignSelf: 'center',
        height: 20,
        fontSize: 9,
        [theme.breakpoints.down('sm')]: {display: 'none'}
    },
    content: {
        minWidth: 0,
        padding: theme.spacing(3.5, 4),
        [theme.breakpoints.down('xs')]: {padding: theme.spacing(2)}
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        paddingBottom: theme.spacing(2.5),
        marginBottom: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    headerContent: {
        minWidth: 0,
        animation: '$headerIn 180ms cubic-bezier(0.2,0,0,1) both'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0
    },
    titleIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
        color: theme.palette.type === 'dark' ? theme.palette.primary.main : '#A38F00',
        '& svg': {fontSize: 22}
    },
    title: {
        fontWeight: 800,
        letterSpacing: '-0.025em'
    },
    description: {
        maxWidth: 720,
        marginTop: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        lineHeight: 1.55
    },
    panel: {
        minWidth: 0,
        animation: '$panelIn 220ms cubic-bezier(0,0.4,0,1) both'
    },
    hidden: {display: 'none'},
    '@keyframes panelIn': {
        from: {opacity: 0},
        to: {opacity: 1}
    },
    '@keyframes headerIn': {
        from: {opacity: 0.35, transform: 'translateY(2px)'},
        to: {opacity: 1, transform: 'translateY(0)'}
    }
});

const useStyles = makeStyles(createSettingsScaffoldStyles);

export default function SettingsScaffold({language, sections, initialKey = 'outlet'}) {
    const classes = useStyles();
    const theme = useTheme();
    const compactNavigation = useMediaQuery(theme.breakpoints.down('sm'));
    const visibleSections = useMemo(() => sections.filter((section) => section.visible !== false), [sections]);
    const [activeKey, setActiveKey] = useState(initialKey);
    const [visited, setVisited] = useState(() => new Set([initialKey]));
    const tabRefs = useRef({});

    useEffect(() => {
        if (!visibleSections.some((section) => section.key === activeKey) && visibleSections.length > 0) {
            setActiveKey(visibleSections[0].key);
        }
    }, [activeKey, visibleSections]);

    const activate = (key) => {
        setActiveKey(key);
        setVisited((current) => {
            const next = new Set(current);
            next.add(key);
            return next;
        });
    };

    const handleTabKey = (event, index) => {
        let nextIndex = index;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % visibleSections.length;
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + visibleSections.length) % visibleSections.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = visibleSections.length - 1;
        else return;
        event.preventDefault();
        const next = visibleSections[nextIndex];
        activate(next.key);
        if (tabRefs.current[next.key]) tabRefs.current[next.key].focus();
    };

    const activeSection = visibleSections.find((section) => section.key === activeKey) || visibleSections[0];
    if (!activeSection) return null;

    return (
        <section className={classes.root} aria-label={language.system_settings || 'System settings'}>
            <aside className={classes.nav}>
                <Typography variant="h5" className={classes.navTitle}>{language.system_settings || 'System Settings'}</Typography>
                <div className={classes.tabList} role="tablist" aria-orientation={compactNavigation ? 'horizontal' : 'vertical'}>
                    {visibleSections.map((section, index) => (
                        <ButtonBase
                            key={section.key}
                            ref={(node) => { tabRefs.current[section.key] = node; }}
                            className={`${classes.navItem} ${activeKey === section.key ? classes.navItemActive : ''}`}
                            role="tab"
                            id={`settings-tab-${section.key}`}
                            aria-selected={activeKey === section.key}
                            aria-controls={`settings-panel-${section.key}`}
                            tabIndex={activeKey === section.key ? 0 : -1}
                            onClick={() => activate(section.key)}
                            onKeyDown={(event) => handleTabKey(event, index)}
                        >
                            <span className={classes.navIcon} aria-hidden="true">{section.icon}</span>
                            <span className={classes.navLabel}>{section.label}</span>
                            {section.badge ? <Chip className={classes.badge} size="small" label={section.badge}/> : null}
                        </ButtonBase>
                    ))}
                </div>
            </aside>
            <main className={classes.content}>
                <header className={classes.header}>
                    <div key={activeSection.key} className={classes.headerContent}>
                        <div className={classes.titleRow}>
                            <span className={classes.titleIcon} aria-hidden="true">{activeSection.icon}</span>
                            <Typography variant="h5" component="h1" className={classes.title}>{activeSection.label}</Typography>
                        </div>
                        <Typography variant="body2" className={classes.description}>{activeSection.description}</Typography>
                    </div>
                </header>
                {visibleSections.map((section) => visited.has(section.key) ? (
                    <div
                        key={section.key}
                        id={`settings-panel-${section.key}`}
                        role="tabpanel"
                        aria-labelledby={`settings-tab-${section.key}`}
                        aria-hidden={activeKey !== section.key}
                        className={activeKey === section.key ? classes.panel : classes.hidden}
                    >
                        {section.content}
                    </div>
                ) : null)}
            </main>
        </section>
    );
}
